import React, { FC, ReactElement, useMemo } from 'react'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import FlexingContainer from '@sport1/news-styleguide/FlexingContainer'
import { HeroVideoProps } from '@sport1/types/web'
import Head from 'next/head'
import { useTheme } from 'styled-components'
import JWPlayer from '@/components/Player/JWPlayer/JWPlayer'
import { getValueByBreakpoint, BreakPointProps } from '@/helpers/breakpoint'
import { transformImageUrlToSize, transformImageUrlToSrcSet } from '@/helpers/imageHelper'
import { BreakPointDisplaySizes } from '@/helpers/pageHelper'
import { SizeTypes } from '@/types/size'
import getVideoLength from '@/utils/video/getVideoLength'
import { useTopMediaDimensions } from '@/hooks/useTopMediaDimensions'
import useVideoPlaylist from '@/hooks/useVideoPlaylist'
import { RecommendedVideos } from '@/helpers/videoHelper/prepareVideoPlaylist'

type Props = {
    content: HeroVideoProps & {
        recommendedVideos?: RecommendedVideos
    }
    isOnLandingPage?: boolean
}

const imgDimensions = {
    height: 'auto',
    width: '100%',
}

const ArticleTopVideo: FC<Props> = ({ content, isOnLandingPage = false }) => {
    const { playlist, runningVideo, setRunningVideo } = useVideoPlaylist({
        video: content,
        recommendedVideos: content.recommendedVideos,
        titleWithDuration: true,
    })
    const { itemDescription, imageUrl } = runningVideo
    const containerRef = React.useRef<(ReactElement & HTMLDivElement) | null>(null)
    const theme = useTheme()
    const isDesktop = useMemo(
        () =>
            getValueByBreakpoint(BreakPointDisplaySizes, theme as BreakPointProps) ===
            SizeTypes.DESKTOP,
        [theme]
    )
    const { width, height } = useTopMediaDimensions()

    const image = transformImageUrlToSize(imageUrl!, width, height)

    const srcSet = transformImageUrlToSrcSet(imageUrl!, width, height)
    const jwPlayer = useMemo(() => {
        return (
            <JWPlayer
                id={runningVideo.id as string}
                autostart={!isOnLandingPage && isDesktop}
                hasRevolverPlay={playlist.length > 0}
                playlist={playlist}
                setRunningVideo={setRunningVideo}
                ad={runningVideo.meta?.ad}
                duration={getVideoLength(runningVideo)}
                trackingData={runningVideo.tracking}
                type="VoD"
                isArticleTopVideo
            />
        )
    }, [isDesktop, isOnLandingPage, playlist, runningVideo, setRunningVideo])

    return (
        <>
            <Head>
                <link rel="preload" fetchpriority="high" href={image} as="image" />
            </Head>
            <NonFlexingContainer innerRef={containerRef} position="relative">
                <FlexingContainer
                    position="absolute"
                    top={0}
                    right={0}
                    left={0}
                    bottom={0}
                    zIndex={6}
                >
                    {jwPlayer}
                </FlexingContainer>
                <img
                    fetchPriority="high"
                    src={image}
                    width={width}
                    height={height}
                    srcSet={srcSet}
                    alt={itemDescription ?? ''}
                    style={imgDimensions}
                />
            </NonFlexingContainer>
        </>
    )
}

export default ArticleTopVideo
